import { useEffect, useMemo, useState } from 'react';
import {
  getAdminPartialPaymentSettings,
  listAdminPartialPaymentProperties,
  saveAdminPartialPaymentSettings,
  saveAdminPropertyPartialPayment,
} from '../../api/adminPartialPayment';
import { useToast } from '../../components/ToastContext';

const modeOptions = [
  { value: 'disabled', label: 'Disabled' },
  { value: 'enabled_for_all_properties', label: 'Enable for all properties' },
  { value: 'enabled_for_selected_properties', label: 'Enable for selected properties' },
];

const defaultSettings = {
  partialPaymentMode: 'disabled',
  allowPartialPaymentDefault: false,
  defaultPartialPaymentPercentage: '',
  allowHostToManagePartialPayment: false,
  minimumPartialPaymentPercentage: '',
  maximumPartialPaymentPercentage: '',
};

const defaultPropertyForm = {
  partialPaymentEnabled: false,
  partialPaymentPercentage: '',
  hostOverrideAllowed: false,
};

const toInputValue = (value) => (value === null || value === undefined || value === '' ? '' : String(value));
const formatPercent = (value) => (value === null || value === undefined || value === '' ? '-' : `${value}%`);
const formatRuleSource = (value) =>
  ({
    admin_global: 'Admin global',
    admin_property: 'Admin property',
    host_property: 'Host property',
    none: '-',
  }[String(value || '').trim().toLowerCase()] || value || '-');
const formatPropertyIdLabel = (property) =>
  property?.isExternal ? property?.providerPropertyId || property?.id : property?.id;
const formatCatalogSource = (property) =>
  property?.isExternal ? `${String(property?.providerCode || 'api').toUpperCase()} API` : 'Homevilas direct';

const AdminPartialPayment = ({ token }) => {
  const toast = useToast();
  const [form, setForm] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [propertiesError, setPropertiesError] = useState('');
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 25 });
  const [editingProperty, setEditingProperty] = useState(null);
  const [propertyForm, setPropertyForm] = useState(defaultPropertyForm);
  const [propertySaving, setPropertySaving] = useState(false);

  const loadSettings = async () => {
    setLoading(true);
    setSettingsError('');
    try {
      const data = await getAdminPartialPaymentSettings(token);
      setForm({
        partialPaymentMode: data?.partialPaymentMode || defaultSettings.partialPaymentMode,
        allowPartialPaymentDefault: !!data?.allowPartialPaymentDefault,
        defaultPartialPaymentPercentage: toInputValue(data?.defaultPartialPaymentPercentage),
        allowHostToManagePartialPayment: !!data?.allowHostToManagePartialPayment,
        minimumPartialPaymentPercentage: toInputValue(data?.minimumPartialPaymentPercentage),
        maximumPartialPaymentPercentage: toInputValue(data?.maximumPartialPaymentPercentage),
      });
      setUpdatedAt(data?.updatedAt || '');
    } catch (err) {
      setSettingsError(err.message || 'Failed to load partial payment settings');
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async (page = pagination.page, query = search) => {
    setPropertiesLoading(true);
    setPropertiesError('');
    try {
      const res = await listAdminPartialPaymentProperties(token, { page, limit: pagination.limit, q: query });
      setProperties(res?.items || []);
      setPagination(res?.pagination || { page: 1, totalPages: 1, total: 0, limit: pagination.limit });
    } catch (err) {
      setPropertiesError(err.message || 'Failed to load properties');
    } finally {
      setPropertiesLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    loadProperties(1, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validationError = useMemo(() => {
    const defaultPct = Number(form.defaultPartialPaymentPercentage || 0);
    const minPct = Number(form.minimumPartialPaymentPercentage || 0);
    const maxPct = Number(form.maximumPartialPaymentPercentage || 0);

    if (!modeOptions.some((opt) => opt.value === form.partialPaymentMode)) {
      return 'Select a valid partial payment mode.';
    }
    if (form.defaultPartialPaymentPercentage !== '') {
      if (!Number.isFinite(defaultPct) || defaultPct <= 0 || defaultPct > 100) {
        return 'Default percentage must be between 1 and 100.';
      }
    }
    if (form.minimumPartialPaymentPercentage !== '') {
      if (!Number.isFinite(minPct) || minPct <= 0 || minPct > 100) {
        return 'Minimum percentage must be between 1 and 100.';
      }
    }
    if (form.maximumPartialPaymentPercentage !== '') {
      if (!Number.isFinite(maxPct) || maxPct <= 0 || maxPct > 100) {
        return 'Maximum percentage must be between 1 and 100.';
      }
    }
    if (form.minimumPartialPaymentPercentage !== '' && form.maximumPartialPaymentPercentage !== '' && minPct > maxPct) {
      return 'Minimum percentage cannot exceed maximum percentage.';
    }
    return '';
  }, [form]);

  const handleSettingsSubmit = async (event) => {
    event.preventDefault();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSaving(true);
    setSettingsError('');
    try {
      const saved = await saveAdminPartialPaymentSettings(token, {
        partialPaymentMode: form.partialPaymentMode,
        allowPartialPaymentDefault: !!form.allowPartialPaymentDefault,
        defaultPartialPaymentPercentage:
          form.defaultPartialPaymentPercentage === '' ? null : Number(form.defaultPartialPaymentPercentage),
        allowHostToManagePartialPayment: !!form.allowHostToManagePartialPayment,
        minimumPartialPaymentPercentage:
          form.minimumPartialPaymentPercentage === '' ? null : Number(form.minimumPartialPaymentPercentage),
        maximumPartialPaymentPercentage:
          form.maximumPartialPaymentPercentage === '' ? null : Number(form.maximumPartialPaymentPercentage),
      });
      setUpdatedAt(saved?.updatedAt || '');
      toast.success('Partial payment settings saved');
      await loadProperties(1, search);
    } catch (err) {
      setSettingsError(err.message || 'Failed to save partial payment settings');
      toast.error(err.message || 'Failed to save partial payment settings');
    } finally {
      setSaving(false);
    }
  };

  const openPropertyEditor = (property) => {
    setEditingProperty(property);
    setPropertyForm({
      partialPaymentEnabled: !!property?.adminSetting?.partialPaymentEnabled,
      partialPaymentPercentage: toInputValue(
        property?.adminSetting?.partialPaymentPercentage ??
          property?.effectiveConfig?.percentage ??
          form.defaultPartialPaymentPercentage
      ),
      hostOverrideAllowed: !!property?.adminSetting?.hostOverrideAllowed,
    });
  };

  const handlePropertySave = async (event) => {
    event.preventDefault();
    if (!editingProperty) return;
    if (propertyForm.partialPaymentEnabled) {
      const nextPercentage = Number(propertyForm.partialPaymentPercentage || 0);
      if (!Number.isFinite(nextPercentage) || nextPercentage <= 0 || nextPercentage > 100) {
        toast.error('Property percentage must be between 1 and 100.');
        return;
      }
    }
    setPropertySaving(true);
    try {
      await saveAdminPropertyPartialPayment(token, editingProperty.id, {
        partialPaymentEnabled: !!propertyForm.partialPaymentEnabled,
        partialPaymentPercentage: propertyForm.partialPaymentEnabled ? Number(propertyForm.partialPaymentPercentage) : null,
        hostOverrideAllowed: editingProperty.isExternal ? false : !!propertyForm.hostOverrideAllowed,
      });
      toast.success('Property override saved');
      await loadProperties(pagination.page, search);
      setEditingProperty(null);
    } catch (err) {
      toast.error(err.message || 'Failed to save property override');
    } finally {
      setPropertySaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="admin-page-title text-2xl font-semibold text-slate-900">Partial payment</h1>
          <p className="text-sm text-slate-600">Control global policy, selected-property overrides, and host edit access.</p>
        </div>
        {updatedAt && <span className="text-xs text-slate-500">Updated {new Date(updatedAt).toLocaleString()}</span>}
      </div>

      {settingsError && <div className="rounded-md bg-rose-50 px-4 py-2 text-sm text-rose-700">{settingsError}</div>}

      {loading ? (
        <div className="admin-card rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
          Loading partial payment settings...
        </div>
      ) : (
        <form
          onSubmit={handleSettingsSubmit}
          className="admin-card rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-4"
        >
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-semibold">Global mode</span>
              <select
                value={form.partialPaymentMode}
                onChange={(e) => setForm((prev) => ({ ...prev, partialPaymentMode: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              >
                {modeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-semibold">Default partial percentage</span>
              <input
                type="number"
                min="1"
                max="100"
                value={form.defaultPartialPaymentPercentage}
                onChange={(e) => setForm((prev) => ({ ...prev, defaultPartialPaymentPercentage: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
                placeholder="25"
              />
            </label>

            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-semibold">Minimum percentage</span>
              <input
                type="number"
                min="1"
                max="100"
                value={form.minimumPartialPaymentPercentage}
                onChange={(e) => setForm((prev) => ({ ...prev, minimumPartialPaymentPercentage: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
                placeholder="10"
              />
            </label>

            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-semibold">Maximum percentage</span>
              <input
                type="number"
                min="1"
                max="100"
                value={form.maximumPartialPaymentPercentage}
                onChange={(e) => setForm((prev) => ({ ...prev, maximumPartialPaymentPercentage: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
                placeholder="50"
              />
            </label>

            <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!form.allowPartialPaymentDefault}
                onChange={(e) => setForm((prev) => ({ ...prev, allowPartialPaymentDefault: e.target.checked }))}
              />
              Allow partial payment by default
            </label>

            <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!form.allowHostToManagePartialPayment}
                onChange={(e) => setForm((prev) => ({ ...prev, allowHostToManagePartialPayment: e.target.checked }))}
              />
              Allow hosts to manage partial payment
            </label>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">Full payment remains available for all properties even when partial payment is enabled.</p>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save settings'}
            </button>
          </div>
        </form>
      )}

      <div className="admin-card rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Selected property overrides</h2>
            <p className="text-sm text-slate-600">Search by property name or property ID and configure an admin override.</p>
          </div>
          <div className="flex gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search property name, local ID, or provider ID"
              className="w-72 rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={() => loadProperties(1, search)}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
            >
              Search
            </button>
          </div>
        </div>

        {propertiesError && <div className="rounded-md bg-rose-50 px-4 py-2 text-sm text-rose-700">{propertiesError}</div>}
        {propertiesLoading ? (
          <div className="text-sm text-slate-500">Loading properties...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Property</th>
                  <th className="px-4 py-3">Property ID</th>
                  <th className="px-4 py-3">Host</th>
                  <th className="px-4 py-3">Effective status</th>
                  <th className="px-4 py-3">Percentage</th>
                  <th className="px-4 py-3">Host override</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {properties.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-4 text-slate-500">
                      No properties found.
                    </td>
                  </tr>
                )}
                {properties.map((property) => (
                  <tr key={property.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{property.title}</div>
                      <div className="mt-1 text-xs text-slate-500">{formatCatalogSource(property)}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatPropertyIdLabel(property)}</td>
                    <td className="px-4 py-3 text-slate-600">{property.isExternal ? 'Elivaas' : property.hostName || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${property.effectiveConfig?.isPartialPaymentAllowed ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                        {property.effectiveConfig?.isPartialPaymentAllowed ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatPercent(property.effectiveConfig?.percentage)}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {property.isExternal ? 'Admin only' : property.adminSetting?.hostOverrideAllowed ? 'Allowed' : 'Locked'}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatRuleSource(property.effectiveConfig?.allowedSource)}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => openPropertyEditor(property)}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} properties)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => loadProperties(pagination.page - 1, search)}
              className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => loadProperties(pagination.page + 1, search)}
              className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {editingProperty && (
        <form
          onSubmit={handlePropertySave}
          className="admin-card rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-4"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Property override</h2>
              <p className="text-sm text-slate-600">
                {editingProperty.title} #{formatPropertyIdLabel(editingProperty)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setEditingProperty(null)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700"
            >
              Close
            </button>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            {editingProperty.effectiveConfig?.notes || 'Set a property-level override for this listing.'}
            {editingProperty.isExternal && (
              <div className="mt-2 text-xs text-slate-500">
                Provider stays use admin-managed overrides only. Host override is not available for external inventory.
              </div>
            )}
          </div>

          <div className={`grid gap-3 ${editingProperty.isExternal ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
            <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!propertyForm.partialPaymentEnabled}
                onChange={(e) => setPropertyForm((prev) => ({ ...prev, partialPaymentEnabled: e.target.checked }))}
              />
              Partial payment enabled
            </label>

            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-semibold">Percentage</span>
              <input
                type="number"
                min="1"
                max="100"
                disabled={!propertyForm.partialPaymentEnabled}
                value={propertyForm.partialPaymentPercentage}
                onChange={(e) => setPropertyForm((prev) => ({ ...prev, partialPaymentPercentage: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </label>

            {!editingProperty.isExternal && (
              <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={!!propertyForm.hostOverrideAllowed}
                  onChange={(e) => setPropertyForm((prev) => ({ ...prev, hostOverrideAllowed: e.target.checked }))}
                />
                Allow host override
              </label>
            )}
          </div>

          <div className="flex items-center justify-end">
            <button
              type="submit"
              disabled={propertySaving}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {propertySaving ? 'Saving...' : 'Save property override'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AdminPartialPayment;
